import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { Camera, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { del, get, post } from '../lib/api.ts'
import { fmtDate } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import { Badge, Button, cn, Dialog, Empty, ErrorNote, Field, Input, PageHeader, Select, Sheet, Spinner } from '../components/ui.tsx'
import { UnitSelect, useUnits } from './portaria.tsx'
import { LOCALE, t } from '../lib/i18n.ts'

type Kind = 'agua' | 'gas' | 'energia'
const KINDS: [Kind, string, string][] = [['agua', t('Água'), 'm³'], ['gas', t('Gás'), 'm³'], ['energia', t('Energia'), 'kWh']]
interface Reading { id: string; unit: { id: string; label: string }; kind: Kind; readOn: string; value: number; reset: boolean; hasPhoto: boolean; note: string | null; createdBy: string | null; createdAt: string; consumption: number | null; days: number | null; mine: boolean }
interface Summary { kind: Kind; unit: string; months: string[]; units: { unitId: string; unitLabel: string; byMonth: Record<string, number | null>; average: number | null; alert: boolean }[] }

const fmtNum = (n: number) => new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 3 }).format(n)
const monthLabel = (ym: string) => new Intl.DateTimeFormat(LOCALE, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(`${ym}-01T12:00:00Z`))
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())

/** Consumo por unidade (issue #14): monthly meter readings with a photo; consumption is the difference between readings. */
export function Consumo() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can } = useCondo(slug)
  const qc = useQueryClient()
  const [kind, setKind] = useState<Kind>('agua')
  const [adding, setAdding] = useState(false)
  const [photo, setPhoto] = useState<Reading | null>(null)
  const staff = can('finance.view')
  const unitOf = KINDS.find((k) => k[0] === kind)![2]
  const list = useQuery({ queryKey: ['readings', condoId, kind], queryFn: () => get<{ readings: Reading[] }>(`/api/condos/${condoId}/readings?kind=${kind}`), enabled: Boolean(condoId) })
  const summary = useQuery({ queryKey: ['readings-summary', condoId, kind], queryFn: () => get<Summary>(`/api/condos/${condoId}/readings/summary?kind=${kind}&months=6`), enabled: Boolean(condoId && staff) })
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['readings', condoId] })
    qc.invalidateQueries({ queryKey: ['readings-summary', condoId] })
  }
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/condos/${condoId}/readings/${id}`), onSuccess: () => { toast.success(t('Leitura removida')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const readings = list.data?.readings ?? []
  return (
    <div>
      <PageHeader title={t('Consumo')}>
        <Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Registrar leitura')}</Button>
      </PageHeader>
      <p className="mb-4 max-w-prose text-tinta-2">{t('Leituras de água, gás e energia por unidade, com foto do medidor. O consumo é a diferença entre duas leituras; um salto fora do padrão costuma ser vazamento.')}</p>
      <div className="mb-4 flex gap-1 border-b border-pauta" role="tablist">
        {KINDS.map(([k, l]) => <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)} className={cn('-mb-px border-b-2 px-3 py-2 font-semibold', kind === k ? 'border-caneta text-caneta' : 'border-transparent text-tinta-2')}>{l}</button>)}
      </div>
      {staff && summary.data && summary.data.units.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-xl font-bold">{t('Consumo por unidade (')}{unitOf})</h2>
          <Sheet className="overflow-x-auto">
            <table className="w-full text-[0.95rem]">
              <thead>
                <tr className="border-b border-pauta text-left text-sm text-tinta-2">
                  <th className="px-3 py-2 font-semibold">{t('Unidade')}</th>
                  {summary.data.months.map((m) => <th key={m} className="px-2 py-2 text-right font-semibold">{monthLabel(m)}</th>)}
                  <th className="px-2 py-2 text-right font-semibold">{t('Média')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.units.map((u) => (
                  <tr key={u.unitId} className="border-b border-pauta last:border-b-0">
                    <td className="px-3 py-2 font-semibold">{u.unitLabel} {u.alert && <Badge tone="margem">{t('atenção')}</Badge>}</td>
                    {summary.data!.months.map((m) => <td key={m} className="px-2 py-2 text-right tabular-nums">{u.byMonth[m] == null ? '–' : fmtNum(u.byMonth[m]!)}</td>)}
                    <td className="px-2 py-2 text-right tabular-nums text-tinta-2">{u.average == null ? '–' : fmtNum(u.average)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sheet>
          <p className="mt-1 text-sm text-tinta-2">{t('"Atenção" marca o último mês mais de 30% acima da média dos anteriores.')}</p>
        </section>
      )}
      <h2 className="mb-2 text-xl font-bold">{t('Leituras')}</h2>
      {list.isLoading ? <Spinner /> : readings.length === 0 ? (
        <Empty title={t('Nenhuma leitura registrada')}>
          {staff ? t('Registre a leitura dos medidores todo mês, ou peça aos moradores: o consumo por unidade aparece aqui.') : t('Registre a leitura do seu medidor todo mês, com uma foto: o consumo aparece aqui e um vazamento é pego cedo.')}
        </Empty>
      ) : (
        <Sheet>
          {readings.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{r.unit.label}: {fmtNum(r.value)} {unitOf} <span className="font-normal text-tinta-2">{t('em')}{' '}{fmtDate(r.readOn)}</span></p>
                <p className="text-sm text-tinta-2">
                  {r.reset ? t('Medidor zerado ou trocado') : r.consumption == null ? t('Primeira leitura') : r.days ? t('Consumo de {amount} {unit} em {days} dias', { amount: fmtNum(r.consumption), unit: unitOf, days: r.days }) : t('Consumo de {amount} {unit}', { amount: fmtNum(r.consumption), unit: unitOf })}
                  {r.createdBy ? t(', por {name}', { name: r.createdBy }) : ''}{r.note ? `. ${r.note}` : ''}
                </p>
              </div>
              {r.hasPhoto && <Button size="sm" variant="quiet" onClick={() => setPhoto(r)}><Camera className="size-4" />{' '}{t('Foto')}</Button>}
              {(staff || r.mine) && <Button size="sm" variant="quiet" onClick={() => confirm(t('Remover esta leitura?')) && remove.mutate(r.id)}>{t('Remover')}</Button>}
            </div>
          ))}
        </Sheet>
      )}
      {adding && <ReadingDialog condoId={condoId} staff={staff} kind={kind} onClose={() => setAdding(false)} onDone={invalidate} />}
      <Dialog open={photo !== null} onClose={() => setPhoto(null)} title={t('Foto do medidor')}>
        {photo && <img src={`/api/condos/${condoId}/readings/${photo.id}/photo`} alt={t('Medidor da unidade {unit}', { unit: photo.unit.label })} className="max-h-[70vh] w-full rounded-ficha object-contain" />}
      </Dialog>
    </div>
  )
}

function ReadingDialog({ condoId, staff, kind, onClose, onDone }: { condoId: string; staff: boolean; kind: Kind; onClose: () => void; onDone: () => void }) {
  const units = useUnits(condoId)
  const [f, setF] = useState({ unitId: '', kind, readOn: today(), value: '', reset: false, note: '' })
  const [file, setFile] = useState<File | null>(null)
  const unitOf = KINDS.find((k) => k[0] === f.kind)![2]
  const m = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      if (f.unitId) fd.set('unitId', f.unitId)
      fd.set('kind', f.kind)
      fd.set('readOn', f.readOn)
      fd.set('value', f.value.trim())
      fd.set('reset', f.reset ? '1' : '0')
      if (f.note.trim()) fd.set('note', f.note.trim())
      if (file) fd.set('file', file)
      return post<{ id: string; consumption: number | null }>(`/api/condos/${condoId}/readings`, fd)
    },
    onSuccess: (r) => { toast.success(r.consumption != null ? t('Leitura registrada. Consumo desde a anterior: {amount} {unit}', { amount: fmtNum(r.consumption), unit: unitOf }) : t('Leitura registrada')); onDone(); onClose() },
  })
  return (
    <Dialog open onClose={onClose} title={t('Registrar leitura')}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <UnitSelect id="rd-u" units={units.data?.units ?? []} staff={staff} value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Medidor')} htmlFor="rd-k"><Select id="rd-k" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Kind })}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
          <Field label={t('Data da leitura')} htmlFor="rd-d"><Input id="rd-d" type="date" value={f.readOn} max={today()} onChange={(e) => setF({ ...f, readOn: e.target.value })} required /></Field>
        </div>
        <Field label={t('Leitura ({unit})', { unit: unitOf })} htmlFor="rd-v" hint={t('Os números do medidor, como aparecem no visor.')}><Input id="rd-v" inputMode="decimal" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} required placeholder={t('Ex.: 1234,567')} /></Field>
        <Field label={t('Foto do medidor (opcional)')} htmlFor="rd-f"><Input id="rd-f" type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
        <Field label={t('Observação (opcional)')} htmlFor="rd-n"><Input id="rd-n" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={200} /></Field>
        <label className="flex items-center gap-2 text-[0.95rem]"><input id="rd-r" type="checkbox" className="size-4" checked={f.reset} onChange={(e) => setF({ ...f, reset: e.target.checked })} />{' '}{t('Medidor zerado ou trocado (não comparar com a leitura anterior)')}</label>
        <ErrorNote error={m.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" busy={m.isPending}>{t('Registrar')}</Button></div>
      </form>
    </Dialog>
  )
}
